/* ═══════════════════════════════════════════════════════════════════════════
   LanguageLive — wrapper for the LIVE language tutor (Modo Professor).

   Owns the VOCABULARY ENGINE around the shared MirrorChat Live pipeline:
   - selects the app-side "word set of the day" (useTutorWordSet) and hands it
     to MirrorChat as `tutorWords` (→ body.tutor_words → tutor delivers it);
   - on the tutor's [[learned:WORD]] success marker, raises mastery (whitelist:
     only words that resolve to a language_vocabulary row are credited) and
     tracks the word for write-back;
   - INCREMENTALLY flushes a `language_sessions` row (session_type 'tutor_live')
     + XP into user_language_progress on every counted marker, with
     visibilitychange/pagehide/unmount as complementary final flushes (iOS
     Safari kills the page without unmounting React — never rely on exit).

   The LLM never decides selection, mastery, or XP — it only DELIVERS and
   SIGNALS. All state lives here / in useLanguageLearning. MirrorChat itself is
   untouched for the Espelho (every tutor path is gated by moduleContext).
   ═══════════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageProvider";
import { LANGUAGE_CODES } from "@/lib/languageActionExecutor";
import * as Sentry from "@sentry/react";
import { createFitnessId as createLanguageId } from '@/lib/fitnessId';
import { LanguageState } from '@/components/language/LanguageState';
import { notifyLanguageChanged } from '@/hooks/useLanguageResource';
import MirrorChat from "./MirrorChat";
import { useRequirePremium } from "@/hooks/useRequirePremium";
import { LIVE_REQUIRES_PREMIUM } from "@/lib/liveAccess";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import {
  useLanguageLearning,
  type LangCode,
} from "@/hooks/useLanguageLearning";
import {
  useTutorWordSet,
  MASTERY_INC,
  type TutorWord,
} from "@/hooks/useTutorWordSet";

// XP credited per word actively practiced (mastery raised) in a live session.
// Aggregated into user_language_progress on exit.
const XP_PER_WORD = 10;
// Floor so a quick session still records a meaningful duration (mirrors
// LanguageLessonView.completeLesson's 60s floor).
const MIN_SESSION_SECONDS = 1;
// Hard cap on how long the kickoff waits for the day-set. The fallback
// generation path (useTutorWordSet → language-lesson edge fn) is an LLM call
// with no client timeout; past this, the kickoff proceeds without words.
const DAY_SET_TIMEOUT_MS = 12_000;

const LanguageLiveContent = ({ learning, targetLanguage }: { learning: ReturnType<typeof useLanguageLearning>; targetLanguage: string }) => {
  // Premium route guard — hoje NEUTRALIZADO por LIVE_REQUIRES_PREMIUM=false
  // (@/lib/liveAccess): o Free entra e usa o tutor live. O hook continua sendo
  // chamado first + unconditionally (rules of hooks) e o redirect só é
  // devolvido DEPOIS de todos os hooks; quando a constante voltar a true, a
  // disciplina de isLoading volta intacta (Premium legítimo nunca é chutado
  // durante o load — mesma disciplina do /mirror/live).
  const { redirect: premiumRedirect } = useRequirePremium();
  const { buildDaySet } = useTutorWordSet(learning);
  const buildDaySetRef = useRef(buildDaySet); buildDaySetRef.current = buildDaySet;

  const {
    profiles,
    progress,
    profileLoading,
    progressLoading,
    vocabularyLoading,
    getVocabularyForLanguage,
    updateMastery,
    addXpToday,
    markPracticedToday,
  } = learning;

  // Resolve the active learning PROFILE first; the tutor's language comes from
  // the profile (language_profiles.learning_language — the same table
  // mirror-chat reads for the tutor persona), NEVER from
  // user_language_progress.active_language. active_language is an aggregate
  // stat that goes stale (prod 10/06: 'nl' while the only profile was 'en'),
  // which pointed the whole day-set pipeline at the wrong vocabulary. It is
  // only used to pick WHICH profile wins when the user has several — it can
  // never select a language that has no profile.
  const activeProfile = useMemo(
    () => profiles.find((p) => p.learning_language === targetLanguage && p.user_id === learning.userId),
    [profiles, learning.userId, targetLanguage],
  );
  const activeLang = (activeProfile?.learning_language ?? null) as LangCode | null;

  const [daySet, setDaySet] = useState<TutorWord[]>([]);
  // Kickoff readiness: true once the day-set build SETTLED (success, empty,
  // failure or timeout). MirrorChat gates the auto-kickoff on this instead of
  // a non-empty set, so a failed/empty word-set can never freeze the opener —
  // the tutor falls back to a generic conversational session.
  const [daySetReady, setDaySetReady] = useState(false);

  // ── Refs for unmount write-back (cleanup closures see stale state) ──
  const daySetPromiseRef = useRef<Promise<TutorWord[]> | null>(null);
  const builtRef = useRef(false);                 // guard double day-set build
  const daySetRef = useRef<TutorWord[]>([]);      // word → id/mastery lookup
  const pendingWordsRef = useRef<Set<string>>(new Set());
  const practicedRef = useRef<Set<string>>(new Set()); // words credited this session
  const sessionStartRef = useRef<number>(Date.now());
  const langRef = useRef<LangCode | null>(activeLang);

  useEffect(() => { daySetRef.current = daySet; }, [daySet]);
  useEffect(() => { langRef.current = activeLang; }, [activeLang]);

  // ── Incremental write-back machinery (declared before handleWordLearned,
  //    which triggers a flush per counted marker) ──
  const sessionRowIdRef = useRef<string>(createLanguageId());   // language_sessions row, insert-once-update-after
  const pendingAggregate = useRef<{ total: number; delta: number } | null>(null);
  const flushedXpRef = useRef(0);                        // XP already aggregated into user_language_progress
  const markedPracticedRef = useRef(false);              // markPracticedToday once per session
  const flushInFlightRef = useRef(false);                // serialize concurrent flushes
  const flushPendingRef = useRef(false);                 // a flush arrived mid-flight → re-run once
  // Ref-held like the old writeBackRef so pagehide/unmount closures are never
  // stale; reassigned every render with fresh addXpToday/markPracticedToday.
  const flushRef = useRef<() => Promise<void>>(async () => {});

  // Live entry analytics (once per mount).
  useEffect(() => { analytics.languageLiveOpened(); }, []);

  // Day-set outcome telemetry — console is invisible on a real iPhone, so
  // every non-ok outcome ships to Sentry (with the useTutorWordSet breadcrumbs
  // explaining WHY) + a PostHog event for funnel counting. No PII beyond the
  // SDK-standard user context.
  const reportDaySet = useCallback(
    (
      status: "ok" | "empty" | "error" | "timeout",
      detail?: { count?: number; reason?: string; language?: string | null },
    ) => {
      analytics.languageDaySetResult(status, {
        count: detail?.count,
        reason: detail?.reason,
        language: detail?.language ?? undefined,
      });
      if (status !== "ok") {
        Sentry.captureMessage("language_tutor.dayset_" + status, {
          level: "warning",
          tags: { feature: "language_tutor" },
          extra: {
            reason: detail?.reason,
            language: detail?.language,
            count: detail?.count,
          },
        });
      }
    },
    [],
  );

  // ── Part 1: build the day-set once profile/progress/VOCAB are loaded ──
  // vocabularyLoading MUST be in the gate: useLanguageLearning.refresh() loads
  // profiles → progress → vocabulary SEQUENTIALLY, so gating only on
  // profile/progress made this effect always run with vocabulary=[] — every
  // user (even with vocab rows) fell into the generateAndSeed edge-fn path,
  // and any failure there left the set empty and the kickoff frozen.
  useEffect(() => {

    if (profileLoading || progressLoading || vocabularyLoading) return;
    if (!activeLang || !activeProfile) {
      // No language picked — tutor still runs, just no set. Unblock the
      // kickoff so the opener fires as a generic study invitation.
      builtRef.current = true;
      setDaySetReady(true);
      return;
    }
    builtRef.current = true;
    let cancelled = false;
    const current = learning.capture();
    (async () => {
      try {
        const set = await (daySetPromiseRef.current ??= buildDaySetRef.current({
          learningLang: activeLang,
          nativeLang: activeProfile.native_language,
          level: activeProfile.level,
        }));
        if (!cancelled && current()) {
          setDaySet(set);
          if (set.length === 0) {
            console.error(
              "[LanguageLive] day-set resolved EMPTY — tutor proceeds conversational (no word drilling this session)",
            );
            reportDaySet("empty", { language: activeLang });
          } else {
            reportDaySet("ok", { count: set.length, language: activeLang });
          }
        }
      } catch (err) {
        if (cancelled || !current()) return;
        console.error("[LanguageLive] buildDaySet failed:", err);
        reportDaySet("error", { reason: String((err as any)?.message ?? err), language: activeLang });
      } finally {
        // Ready on EVERY outcome — the kickoff must never wait forever.
        if (!cancelled && current()) setDaySetReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [profileLoading, progressLoading, vocabularyLoading, activeLang, activeProfile?.native_language, activeProfile?.level, reportDaySet]);

  // Day-set watchdog: if the build (or the loading gates above) hasn't
  // settled within DAY_SET_TIMEOUT_MS, release the kickoff anyway.
  useEffect(() => {
    const current = learning.capture();
    const tm = window.setTimeout(() => {
      if (!current()) return;
      setDaySetReady((ready) => {
        if (!ready) {
          console.error(
            "[LanguageLive] day-set build timed out after " + DAY_SET_TIMEOUT_MS + "ms — kickoff proceeding without word set",
          );
          reportDaySet("timeout", { language: langRef.current });
        }
        return true;
      });
    }, DAY_SET_TIMEOUT_MS);
    return () => clearTimeout(tm);
  }, [reportDaySet]);

  // ── Part 3: tutor signalled a learned word → whitelist, mastery, flush ──
  const handleWordLearned = useCallback(
    (word: string) => {
      const key = word.trim().toLowerCase();
      if (!key || practicedRef.current.has(key) || pendingWordsRef.current.has(key)) return;

      // Anti-farm whitelist: only words that resolve to a language_vocabulary
      // row (day-set first, wider vocab of the active language as fallback)
      // are credited. A marker for an improvised word (prod 10/06: the tutor
      // credited "competition"/"profit" while the real set was elsewhere)
      // earns nothing — XP must always be backed by a mastery write.
      const fromSet = daySetRef.current.find((w) => w.word.trim().toLowerCase() === key);
      let id = fromSet?.id ?? null;
      let mastery = fromSet?.mastery ?? 0;
      if (!id && langRef.current) {
        const hit = getVocabularyForLanguage(langRef.current).find(
          (v) => v.word.trim().toLowerCase() === key,
        );
        if (hit) { id = hit.id; mastery = hit.mastery; }
      }
      if (!id) {
        console.warn("[LanguageLive] [[learned]] marker outside the user's vocab — not credited:", word);
        Sentry.addBreadcrumb({
          category: "language_tutor",
          message: "learned_marker_outside_vocab",
          level: "warning",
          data: { word: key, language: langRef.current },
        });
        return;
      }

      const current = learning.capture();
      if (!current()) return;
      pendingWordsRef.current.add(key);
      void updateMastery(id, mastery + MASTERY_INC).then(confirmed => {
        if (!current() || !confirmed) return;
        practicedRef.current.add(key);
        void flushRef.current();
      }).finally(() => pendingWordsRef.current.delete(key));
    },
    [getVocabularyForLanguage, updateMastery, learning.capture],
  );

  // ── Part 4: INCREMENTAL write-back (session row + XP aggregate) ──
  // iOS Safari/Capacitor kills the page WITHOUT unmounting React, so an
  // unmount-only write-back can lose the entire session. Strategy: flush on
  // every counted marker (a session has at most ~DAY_SET_SIZE of them), with
  // visibilitychange/pagehide + unmount as complementary FINAL flushes —
  // never the only trigger. First flush inserts the language_sessions row;
  // later flushes update it in place (duration/xp/practiced list grow).
  flushRef.current = async () => {
    if (flushInFlightRef.current) { flushPendingRef.current = true; return; }
    flushInFlightRef.current = true;
    try {
      const practiced = Array.from(practicedRef.current);
      const lang = langRef.current;
      // Nothing credited yet — intentionally no empty session row (a silent
      // return here masked the inert-spine bug for weeks, so: say so).
      if (practiced.length === 0 || !lang) return;

      const current = learning.capture(), ownerId = learning.userId;
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !current() || !ownerId || user?.id !== ownerId) return;

      const durationSeconds = Math.max(
        MIN_SESSION_SECONDS,
        Math.floor((Date.now() - sessionStartRef.current) / 1000),
      );
      const xpEarned = practiced.length * XP_PER_WORD;

      const payload = { id: sessionRowIdRef.current, user_id: ownerId, session_type: 'tutor_live', language: lang, duration_seconds: durationSeconds, xp_earned: xpEarned, vocabulary_practiced: practiced };
      const inserted = await (supabase as any).from('language_sessions').upsert(payload, { onConflict: 'id', ignoreDuplicates: true }).select('id');
      if (!current() || inserted.error) throw inserted.error || new Error('Language account changed');
      const result = await (supabase as any).from('language_sessions').update({ duration_seconds: durationSeconds, xp_earned: xpEarned, vocabulary_practiced: practiced }).eq('id', sessionRowIdRef.current).eq('user_id', ownerId).eq('session_type', 'tutor_live').eq('language', lang).select().single();
      if (!current() || result.error || result.data?.user_id !== ownerId || result.data?.xp_earned !== xpEarned || result.data?.duration_seconds !== durationSeconds) throw result.error || new Error('Live session not confirmed');
      notifyLanguageChanged();

      // Aggregate ONLY the delta since the last successful flush into
      // user_language_progress, so incremental flushes never double-credit.
      // Flushes are serialized (flushInFlightRef) and markers arrive one
      // assistant-turn apart, so the progress state addXpToday reads has
      // settled between flushes.
      const aggregate = pendingAggregate.current ?? { total: xpEarned, delta: xpEarned - flushedXpRef.current };
      if (aggregate.delta > 0) {
        pendingAggregate.current = aggregate;
        await addXpToday(aggregate.delta, `${sessionRowIdRef.current}:${aggregate.total}`);
        if (!current()) return;
        flushedXpRef.current = aggregate.total; pendingAggregate.current = null;
        if (xpEarned > aggregate.total) flushPendingRef.current = true;
      }
      if (!markedPracticedRef.current) {
        await markPracticedToday();
        if (!current()) return;
        markedPracticedRef.current = true;
      }
    } catch (err) {
      console.error("[LanguageLive] write-back flush threw:", err);
    } finally {
      flushInFlightRef.current = false;
      if (flushPendingRef.current) {
        flushPendingRef.current = false;
        void flushRef.current();
      }
    }
  };

  useEffect(() => {
    // Complementary FINAL flushes. beforeunload is deliberately not used —
    // it's unreliable on iOS; pagehide + visibilitychange(hidden) are the
    // recommended pair there. The unmount cleanup still covers in-app SPA
    // navigation away from /language/live.
    const finalFlush = () => { void flushRef.current(); };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") finalFlush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", finalFlush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", finalFlush);
      finalFlush();
    };
  }, []);

  // Shape the day-set for MirrorChat → body.tutor_words (id/mastery stay here).
  const tutorWords = useMemo(
    () => daySet.map((w) => ({ word: w.word, translation: w.translation, example: w.example })),
    [daySet],
  );

  // Defense-in-depth de ROTA (direct URL / deep-link / cached route), gêmeo do
  // guard inline do /mirror/live em MirrorChat (que é escopado a !moduleContext
  // e nunca cobriu este caminho). Hoje LIVE_REQUIRES_PREMIUM=false o desliga —
  // Free entra; com true ele volta a fechar a rota sozinho.
  if (LIVE_REQUIRES_PREMIUM && premiumRedirect) return premiumRedirect;

  return (
    <MirrorChat
      live
      moduleContext="language_tutor"
      tutorLanguage={activeLang || undefined}
      tutorWords={tutorWords}
      tutorWordsReady={daySetReady}
      onWordLearned={handleWordLearned}
      autoKickoff
    />
  );
};

const LanguageLive = () => {
  const learning = useLanguageLearning(), [params] = useSearchParams(), { t } = useLanguage();
  if (!learning.userId || !learning.hasData || learning.loadError) return <LanguageState learning={learning}/>;
  // An explicit target never falls back to a different profile. Legacy module
  // navigation without a query still uses the current, uniquely owned profile.
  const explicit = params.has('lang'), language = explicit ? params.get('lang') : learning.profile?.learning_language;
  const valid = typeof language === 'string' && (LANGUAGE_CODES as readonly string[]).includes(language) && params.getAll('lang').length <= 1 && (explicit || learning.profile?.user_id === learning.userId);
  const matches = valid ? learning.profiles.filter(p => p.learning_language === language) : [];
  const target = matches.length === 1 && matches[0].user_id === learning.userId ? matches[0] : null;
  if (!target) return <main className="language-page p-6"><p role="alert">{t(valid || !explicit ? 'languageClassAction.tutorProfileMissing' : 'languageClassAction.tutorInvalidLanguage')}</p></main>;
  return <LanguageLiveContent key={`${learning.userId}:${target.learning_language}`} learning={learning} targetLanguage={target.learning_language}/>;
};
export default LanguageLive;
